import React, { useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import FullPageLoader from '../../components/common/FullPageLoader';
import { AllForexComplianceView, ForexComplianceDetail } from '../../features/forex-compliance';

const FOREX_COMPLIANCE_QUERY = gql`
  query ForexComplianceRecords($limit: Int!, $offset: Int!) {
    forexComplianceRecords(limit: $limit, offset: $offset) {
      id
      orderId: id
      buyer
      supplier
      country
      currency
      totalValue
      incoterms
      expectedShipmentDate
      complianceStatus
      isLocked
      checklist {
        requirement
        status
        file
        notes
        required
        expiryDate
        owner
        requestedOn
        uploadedOn
        approvedOn
      }
      remittancePlan {
        totalAmount
        tranches {
          percentage
          amount
          date
          milestone
        }
      }
      bankMetadata {
        adBankName
        adBankCode
        rbiApprovalNumber
        femaReference
        bankSubmissionRef
        declarationDate
      }
      approvalHistory {
        timestamp
        action
        by
        reason
        newStatus
      }
    }
  }
`;

type ComplianceStatus = 'draft' | 'submitted' | 'docs-requested' | 'docs-resubmitted' | 'compliance-approved' | 'remittance-initiated' | 'remittance-completed' | 'locked';

interface ForexComplianceRecord {
  id: string;
  orderId: string;
  buyer: string;
  supplier: string;
  country: string;
  currency: string;
  totalValue: number;
  incoterms: string;
  expectedShipmentDate?: string | null;
  complianceStatus: ComplianceStatus;
  isLocked: boolean;
  checklist: Array<{
    requirement: string;
    status: string;
    file?: string | null;
    notes?: string | null;
    required: boolean;
    expiryDate?: string | null;
    owner: 'supplier' | 'finance' | 'ops' | 'agent';
    requestedOn?: string | null;
    uploadedOn?: string | null;
    approvedOn?: string | null;
  }>;
  remittancePlan: {
    totalAmount: number;
    tranches: Array<{
      percentage: number;
      amount: number;
      date?: string | null;
      milestone: string;
    }>;
  };
  bankMetadata: {
    adBankName: string;
    adBankCode: string;
    rbiApprovalNumber?: string | null;
    femaReference?: string | null;
    bankSubmissionRef?: string | null;
    declarationDate?: string | null;
  };
  approvalHistory: Array<{
    timestamp: string;
    action: string;
    by: string;
    reason?: string | null;
    newStatus?: ComplianceStatus | null;
  }>;
}

interface ForexComplianceQueryData {
  forexComplianceRecords: ForexComplianceRecord[];
}

interface ForexCompliancePageProps {
  initialOrderId?: string;
}

export const ForexCompliancePage: React.FC<ForexCompliancePageProps> = ({ initialOrderId }) => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(initialOrderId || null);
  const { data, loading, refetch } = useGraphQLQuery<ForexComplianceQueryData>(FOREX_COMPLIANCE_QUERY, {
    variables: { limit: 200, offset: 0 },
    showErrorToast: false,
    pollInterval: 4000,
    fetchPolicy: 'network-only',
  });

  const records = data?.forexComplianceRecords || [];
  const orders = useMemo(
    () => records.map((record) => ({
      id: record.id,
      buyer: record.buyer,
      supplier: record.supplier,
      country: record.country,
      currency: record.currency,
      totalValue: record.totalValue,
      complianceStatus: record.complianceStatus,
      expectedShipmentDate: record.expectedShipmentDate || '',
    })),
    [records]
  );
  const selectedRecord = records.find((record) => record.id === selectedOrderId) || undefined;

  React.useEffect(() => {
    if (initialOrderId) {
      setSelectedOrderId(initialOrderId);
    }
  }, [initialOrderId]);

  if (loading && records.length === 0) {
    return <FullPageLoader />;
  }

  if (selectedOrderId) {
    return (
      <ForexComplianceDetail 
        selectedOrderId={selectedOrderId}
        onBack={() => setSelectedOrderId(null)}
        orders={orders}
        initialData={selectedRecord as any}
        onRecordUpdated={() => refetch({ limit: 200, offset: 0 })}
      />
    );
  }

  return (
    <AllForexComplianceView 
      orders={orders}
      onSelectOrder={setSelectedOrderId}
    />
  );
};
